import React, { useCallback, useEffect, useState } from "react";
import { Card, Table, Select, Button, Alert, Empty, Progress, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import appVersionGateApi from "../../../api/appVersionGateApi";
import OtaRolloutStatTiles from "./ota-rollout-stat-tiles";
import OtaRolloutExplainer from "./ota-rollout-explainer";
import buildOtaColumns from "./ota-rollout-columns";

/**
 * "Ai đã nhận bản vá OTA, ai chưa."
 *
 * Phân biệt với tab kia: tab kia đặt version BINARY trên store (ép user đi tải lại
 * app), bảng này đo bản JS mà hot-updater đã giao tới máy — version binary KHÔNG
 * đổi khi đẩy OTA.
 *
 * Mẫu số là user có MỞ APP trong N ngày, không phải tổng số user: chưa mở app thì
 * không thể nhận OTA, đếm vào chỉ làm tỷ lệ xấu đi một cách vô nghĩa.
 */

const DAY_OPTIONS = [
    { value: 7, label: "7 ngày" },
    { value: 14, label: "14 ngày" },
    { value: 30, label: "30 ngày" },
    { value: 90, label: "90 ngày" },
];

const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);
const vn = (n) => Number(n || 0).toLocaleString("vi-VN");

export default function OtaRolloutPanel() {
    const [days, setDays] = useState(14);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const fetchStats = useCallback(async (d) => {
        setLoading(true);
        try {
            const res = await appVersionGateApi.getClientStats(d);
            setData(res.data?.data || null);
        } catch {
            message.error("Không tải được thống kê bản OTA");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats(days);
    }, [days, fetchStats]);

    const totals = data?.totals || {
        users: 0,
        ota: 0,
        builtin: 0,
        unknown: 0,
        eligible: 0,
        legacy: 0,
        otaEligible: 0,
    };
    const minVer = data?.otaCapableMinVersion || "1.7.0";

    const columns = buildOtaColumns(totals);

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="text-sm text-content-3">
                    Bản vá nóng đã về tới máy user — ai nhận rồi, ai chưa. Chi tiết xem ghi chú bên
                    dưới.
                </div>
                <div className="flex items-center gap-2">
                    <Select value={days} onChange={setDays} options={DAY_OPTIONS} style={{ width: 120 }} />
                    <Button
                        icon={<FontAwesomeIcon icon={faRotate} />}
                        onClick={() => fetchStats(days)}
                        loading={loading}
                    />
                </div>
            </div>

            <OtaRolloutExplainer />

            <OtaRolloutStatTiles totals={totals} />

            {/*
                HAI tỷ lệ, mẫu số khác nhau, đừng gộp:
                  A đo "user đã chịu tải bản app mới chưa" — là con số quyết định bao giờ
                    tắt được EAS Update cho nhánh cũ.
                  B đo "bản vá lan tới đâu" trong nhóm thực sự nhận được OTA. Lấy mẫu số
                    là TOÀN BỘ máy thì tỷ lệ không bao giờ chạm 90% vì luôn còn máy đời cũ.
            */}
            <Card size="small" className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                    <span>
                        Độ phủ bản store (≥ {minVer}): <b>{vn(totals.eligible)}</b> /{" "}
                        {vn(totals.users)} máy
                    </span>
                    <span className="text-content-3">{pct(totals.eligible, totals.users)}%</span>
                </div>
                <Progress percent={pct(totals.eligible, totals.users)} status="active" />
                <div className="text-xs text-content-3 mt-1 mb-3">
                    Còn <b>{vn(totals.legacy)}</b> máy ở bản cũ hơn {minVer} — nhóm này KHÔNG nhận
                    được OTA, chỉ vá nóng qua EAS Update ở nhánh <code>maintenance/1.6.x</code>.
                </div>

                <div className="flex justify-between text-sm mb-1">
                    <span>
                        Độ phủ bản vá OTA: <b>{vn(totals.otaEligible)}</b> / {vn(totals.eligible)}{" "}
                        máy đủ điều kiện
                    </span>
                    <span className="text-content-3">
                        {pct(totals.otaEligible, totals.eligible)}%
                    </span>
                </div>
                <Progress
                    percent={pct(totals.otaEligible, totals.eligible)}
                    status="active"
                    strokeColor="#10b981"
                />
            </Card>

            {totals.users === 0 && !loading && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Chưa có số liệu"
                    description="Bảng chỉ có dòng sau khi user mở app và gọi get-me. Mới bật tính năng thì chờ vài phút."
                />
            )}

            <Table
                rowKey={(r) => `${r.bundleId || "none"}-${r.platform}`}
                columns={columns}
                dataSource={data?.bundles || []}
                loading={loading}
                pagination={false}
                size="small"
                scroll={{ x: 1100 }}
                locale={{ emptyText: <Empty description="Chưa có máy nào báo bundle" /> }}
            />
        </div>
    );
}
